import { getFormProps, useForm, useInputControl } from '@conform-to/react';
import { useEffect, type ReactNode } from 'react';
import { useFetcher } from 'react-router';
import { useAuthenticityToken } from 'remix-utils/csrf/react';

import { StripeElementsProvider, StripePaymentForm } from '~/components/payments/stripe-form.client';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Modal } from '~/components/ui/modal';
import { StatusButton } from '~/components/ui/status-button';
import { type action as confirmNonStripePaymentAction } from '~/routes/resources+/confirm-non-stripe-payment';
import { cn } from '~/styles/extended-theme';
import { fromStripeFormat } from '~/utils/currency';

export interface PromoCode {
  name: string;
  type: string;
  amount: number;
  appliesToFirstPaymentOnly?: boolean | null;
}

interface PurchaseModalProps {
  stripeClientSecret: string | null;
  open: boolean;
  onClose: () => void;
  returnUrl: string;
  title: string;
  content: ReactNode;
  className?: string;
  onPrePaymentCheck?: () => Promise<string | null>;
  stripePaymentButtonLabel?: string;
  nonStripePaymentButtonLabel?: string;
  nonStripePaymentReturnUrl?: string;
  nonStripePaymentSuccessMessage?: string;
  onNonStripePaymentConfirmed?: () => void;
  onPromoCodeSubmit: (promoCode: string) => void;
  promoCode?: PromoCode;
  promoCodeInvalid?: boolean;
  isLoading?: boolean;
  helperContent?: ReactNode;
  paymentId?: string;
  intentType?: 'payment' | 'setup';
  registrationCount?: number;
}

export function DefaultPromoCodeHelperContent({
  amount,
  amountBelowMinimum,
  promoOnFirstPaymentOnly,
  recurring = false,
}: {
  amount: number | undefined;
  amountBelowMinimum: boolean | undefined;
  promoOnFirstPaymentOnly?: boolean | null;
  recurring?: boolean;
}) {
  const lines = [];

  if (promoOnFirstPaymentOnly) {
    lines.push('Discount applied to first payment only.');
  }

  if (amountBelowMinimum) {
    lines.push(
      'Your promo code has reduced the item price below our minimum for online processing. The final price has been adjusted to match the online minimum.',
    );
  }

  if (recurring) {
    lines.push(
      'By providing your card information, you allow Fighters Passport to charge your card for future payments in accordance with their terms.',
    );
  }

  return (
    <div className="ml-1 mt-2 flex flex-col gap-2 text-body2 text-blue-400">
      <p>{lines.join(' ')}</p>
      {amount === 0 ? (
        <>
          <p>Your promo code has reduced the cost of this item to $0.00</p>
          <p>No service fees will be charged upon purchase.</p>
        </>
      ) : null}
    </div>
  );
}

export function PurchaseModal({
  onClose,
  stripeClientSecret,
  open,
  returnUrl,
  title,
  content,
  className,
  onPrePaymentCheck,
  stripePaymentButtonLabel,
  nonStripePaymentButtonLabel = 'Redeem at no Cost',
  nonStripePaymentReturnUrl,
  nonStripePaymentSuccessMessage,
  onNonStripePaymentConfirmed,
  onPromoCodeSubmit,
  promoCode,
  promoCodeInvalid,
  isLoading,
  helperContent,
  paymentId,
  intentType = 'payment',
  registrationCount,
}: PurchaseModalProps) {
  const csrf = useAuthenticityToken();
  const [promoCodeForm, promoCodeFields] = useForm({
    defaultValue: {
      promoCode: promoCode?.name ?? '',
    },
    onSubmit: (event) => {
      event.preventDefault();
      onPromoCodeSubmit(promoCodeFields.promoCode.value ?? '');
    },
  });
  const promoCodeControl = useInputControl(promoCodeFields.promoCode);

  const confirmNonStripePaymentFetcher = useFetcher<typeof confirmNonStripePaymentAction>();

  const isSubmittingNonStripePayment = confirmNonStripePaymentFetcher.state !== 'idle';

  const handleNonStripePaymentConfirmed = () => {
    if (paymentId) {
      const formData: Record<string, string> = {
        csrf,
        paymentId,
      };

      if (nonStripePaymentReturnUrl) {
        formData.redirectUrl = nonStripePaymentReturnUrl;
      }

      if (nonStripePaymentSuccessMessage) {
        formData.successMessage = nonStripePaymentSuccessMessage;
      }

      confirmNonStripePaymentFetcher.submit(formData, {
        method: 'patch',
        action: '/resources/confirm-non-stripe-payment',
      });
    }
  };

  useEffect(() => {
    if (confirmNonStripePaymentFetcher.state === 'idle' && confirmNonStripePaymentFetcher.data) {
      if (onNonStripePaymentConfirmed) {
        onNonStripePaymentConfirmed();
      } else {
        onClose();
      }
    }
  }, [
    confirmNonStripePaymentFetcher.state,
    confirmNonStripePaymentFetcher.data,
    onClose,
    onNonStripePaymentConfirmed,
  ]);

  return (
    <Modal
      title={title}
      open={open}
      onClose={onClose}
      className={cn('max-h-[70vh] overflow-y-auto', className)}
    >
      {content}
      <div className="mb-4 ml-1 mt-4">
        <form {...getFormProps(promoCodeForm)}>
          <Label htmlFor="promo-code-input" className="text-body2">
            Have a code? Enter it here.
          </Label>
          <div className="flex flex-row items-center gap-4 pr-8">
            <Input
              variant="rounded"
              id="promo-code-input"
              placeholder="Enter your promo code"
              className={cn(
                'rounded-sm border-gray-400',
                'hover:border-b-1',
                'focus-visible:border-b-1 focus-visible:outline-0',
              )}
              value={promoCodeControl.value}
              onChange={(e) => promoCodeControl.change(e.target.value)}
            />
            <StatusButton
              type="submit"
              disabled={
                !promoCodeForm.dirty ||
                isLoading ||
                !promoCodeControl.value ||
                promoCodeControl.value === promoCode?.name
              }
              status={isLoading ? 'pending' : 'idle'}
            >
              Apply
            </StatusButton>
          </div>
          <div className="mt-1 text-caption uppercase">
            {promoCodeInvalid ? (
              <p className="text-destructive">This promo code is invalid</p>
            ) : null}
            {promoCode ? (
              <div className="text-success">
                <p>
                  Promo code applied &#x2022;{' '}
                  {promoCode.type === 'amount' && promoCode.amount
                    ? fromStripeFormat(promoCode.amount)
                    : null}
                  {promoCode.type === 'percentage' ? `${promoCode.amount}%` : null}
                  {registrationCount && registrationCount > 1 ? ' per ticket' : null}
                  {promoCode.appliesToFirstPaymentOnly ? <> &#x2022; One time only</> : null}
                </p>
              </div>
            ) : null}
          </div>
        </form>
      </div>
      {stripeClientSecret ? (
        <StripeElementsProvider key={stripeClientSecret} stripeClientSecret={stripeClientSecret}>
          <StripePaymentForm
            returnUrl={returnUrl}
            onPrePaymentCheck={onPrePaymentCheck}
            stripePaymentButtonLabel={stripePaymentButtonLabel}
            disabled={isLoading}
            intentType={intentType}
          >
            {helperContent}
          </StripePaymentForm>
        </StripeElementsProvider>
      ) : null}
      {!stripeClientSecret && paymentId ? (
        <>
          {helperContent}
          <div className="mt-6 flex justify-end">
            <StatusButton
              disabled={isLoading || isSubmittingNonStripePayment}
              status={isSubmittingNonStripePayment ? 'pending' : 'idle'}
              onClick={handleNonStripePaymentConfirmed}
            >
              {nonStripePaymentButtonLabel}
            </StatusButton>
          </div>
        </>
      ) : null}
    </Modal>
  );
}
